<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import FormInput from 'dashboard/components-next/input/Input.vue';
import AccountHolidaysAPI from 'dashboard/api/accountHolidays';
import BusinessHoursTimePicker from './BusinessHoursTimePicker.vue';
import HolidayDatePicker from './HolidayDatePicker.vue';

const { t } = useI18n();

const holidays = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeletingId = ref(null);
const editingId = ref(null);
const form = ref({
  name: '',
  start_date: null,
  start_time: '09:00 AM',
  end_date: null,
  end_time: '05:00 PM',
});

const toDateOrNull = value => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDateTime = value => {
  const dateValue = toDateOrNull(value);
  if (!dateValue) return '';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateValue);
};

const parseTimeValue = value => {
  const dateValue = toDateOrNull(value);
  if (!dateValue) return '09:00 AM';

  let hours = dateValue.getHours();
  const minutes = dateValue.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';

  hours %= 12;
  if (hours === 0) hours = 12;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )} ${period}`;
};

const parseTimeParts = value => {
  const [time = '09:00', period = 'AM'] = (value || '09:00 AM').split(' ');
  const [hourValue = '09', minuteValue = '00'] = time.split(':');

  let hour = Number.parseInt(hourValue, 10);
  const minute = Number.parseInt(minuteValue, 10) || 0;
  const normalizedPeriod = period === 'PM' ? 'PM' : 'AM';

  if (Number.isNaN(hour)) hour = 9;
  if (normalizedPeriod === 'PM' && hour !== 12) hour += 12;
  if (normalizedPeriod === 'AM' && hour === 12) hour = 0;

  return { hour, minute };
};

const combineDateAndTime = (dateValue, timeValue) => {
  const normalizedDate = toDateOrNull(dateValue);
  if (!normalizedDate) return null;

  const { hour, minute } = parseTimeParts(timeValue);
  const combinedDate = new Date(normalizedDate);
  combinedDate.setHours(hour, minute, 0, 0);

  return combinedDate;
};

const startDateTime = computed(() =>
  combineDateAndTime(form.value.start_date, form.value.start_time)
);

const endDateTime = computed(() =>
  combineDateAndTime(form.value.end_date, form.value.end_time)
);

const serializedForm = computed(() => ({
  name: form.value.name.trim(),
  start_date: startDateTime.value?.toISOString(),
  end_date: endDateTime.value?.toISOString(),
}));

const isEditing = computed(() => editingId.value !== null);
const formInvalid = computed(
  () =>
    !form.value.name.trim() ||
    !form.value.start_date ||
    !form.value.end_date ||
    !form.value.start_time ||
    !form.value.end_time ||
    endDateTime.value.getTime() < startDateTime.value.getTime()
);

const resetForm = () => {
  editingId.value = null;
  form.value = {
    name: '',
    start_date: null,
    start_time: '09:00 AM',
    end_date: null,
    end_time: '05:00 PM',
  };
};

const loadHolidays = async () => {
  isLoading.value = true;
  try {
    const response = await AccountHolidaysAPI.get();
    holidays.value = response.data.payload || [];
  } finally {
    isLoading.value = false;
  }
};

const saveHoliday = async () => {
  if (formInvalid.value) return;

  isSaving.value = true;
  try {
    const payload = { account_holiday: serializedForm.value };
    if (editingId.value) {
      await AccountHolidaysAPI.update(editingId.value, payload);
      useAlert(t('BUSINESS_HOURS.HOLIDAYS.API.UPDATE_SUCCESS'));
    } else {
      await AccountHolidaysAPI.create(payload);
      useAlert(t('BUSINESS_HOURS.HOLIDAYS.API.CREATE_SUCCESS'));
    }
    resetForm();
    await loadHolidays();
  } catch {
    useAlert(t('BUSINESS_HOURS.HOLIDAYS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const editHoliday = holiday => {
  editingId.value = holiday.id;
  form.value = {
    name: holiday.name,
    start_date: toDateOrNull(holiday.start_date),
    start_time: parseTimeValue(holiday.start_date),
    end_date: toDateOrNull(holiday.end_date),
    end_time: parseTimeValue(holiday.end_date),
  };
};

const deleteHoliday = async holidayId => {
  isDeletingId.value = holidayId;
  try {
    await AccountHolidaysAPI.delete(holidayId);
    holidays.value = holidays.value.filter(holiday => holiday.id !== holidayId);
    useAlert(t('BUSINESS_HOURS.HOLIDAYS.API.DELETE_SUCCESS'));
    if (editingId.value === holidayId) resetForm();
  } catch {
    useAlert(t('BUSINESS_HOURS.HOLIDAYS.API.ERROR'));
  } finally {
    isDeletingId.value = null;
  }
};

onMounted(loadHolidays);
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-5">
    <div
      class="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)]"
    >
      <FormInput
        v-model="form.name"
        :label="t('BUSINESS_HOURS.HOLIDAYS.FORM.NAME')"
        :placeholder="t('BUSINESS_HOURS.HOLIDAYS.FORM.NAME_PLACEHOLDER')"
      />
      <div class="min-w-0">
        <label class="mb-2 block text-label-small font-medium text-n-slate-11">
          {{ t('BUSINESS_HOURS.HOLIDAYS.FORM.START_DATE') }}
        </label>
        <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,9rem)] gap-3">
          <div class="min-w-0">
            <HolidayDatePicker
              v-model="form.start_date"
              :placeholder="t('BUSINESS_HOURS.HOLIDAYS.FORM.START_DATE')"
            />
          </div>
          <div class="min-w-0">
            <BusinessHoursTimePicker
              v-model="form.start_time"
              :placeholder="t('BUSINESS_HOURS.SELECT_TIME')"
            />
          </div>
        </div>
      </div>
      <div class="min-w-0">
        <label class="mb-2 block text-label-small font-medium text-n-slate-11">
          {{ t('BUSINESS_HOURS.HOLIDAYS.FORM.END_DATE') }}
        </label>
        <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,9rem)] gap-3">
          <div class="min-w-0">
            <HolidayDatePicker
              v-model="form.end_date"
              :placeholder="t('BUSINESS_HOURS.HOLIDAYS.FORM.END_DATE')"
            />
          </div>
          <div class="min-w-0">
            <BusinessHoursTimePicker
              v-model="form.end_time"
              :placeholder="t('BUSINESS_HOURS.SELECT_TIME')"
            />
          </div>
        </div>
        <p
          v-if="
            startDateTime &&
            endDateTime &&
            endDateTime.getTime() < startDateTime.getTime()
          "
          class="mb-0 mt-2 text-sm text-n-ruby-9"
        >
          {{ t('BUSINESS_HOURS.HOLIDAYS.FORM.DATE_ERROR') }}
        </p>
      </div>
    </div>

    <div class="flex items-center gap-3">
      <Button
        sm
        color="blue"
        :label="
          isEditing
            ? t('BUSINESS_HOURS.HOLIDAYS.FORM.UPDATE')
            : t('BUSINESS_HOURS.HOLIDAYS.FORM.ADD')
        "
        :disabled="formInvalid"
        :is-loading="isSaving"
        @click="saveHoliday"
      />
      <Button
        v-if="isEditing"
        sm
        slate
        outline
        :label="t('BUSINESS_HOURS.HOLIDAYS.FORM.CANCEL')"
        @click="resetForm"
      />
    </div>

    <div class="rounded-2xl border border-n-weak bg-n-surface-1">
      <div
        class="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 border-b border-n-weak px-4 py-3 text-xs font-medium uppercase tracking-wide text-n-slate-11"
      >
        <span>{{ t('BUSINESS_HOURS.HOLIDAYS.TABLE.NAME') }}</span>
        <span>{{ t('BUSINESS_HOURS.HOLIDAYS.TABLE.START_DATE') }}</span>
        <span>{{ t('BUSINESS_HOURS.HOLIDAYS.TABLE.END_DATE') }}</span>
        <span>{{ t('BUSINESS_HOURS.HOLIDAYS.TABLE.ACTIONS') }}</span>
      </div>

      <div v-if="isLoading" class="px-4 py-6 text-sm text-n-slate-11">
        {{ t('BUSINESS_HOURS.HOLIDAYS.LOADING') }}
      </div>
      <div
        v-else-if="!holidays.length"
        class="px-4 py-6 text-sm text-n-slate-11"
      >
        {{ t('BUSINESS_HOURS.HOLIDAYS.EMPTY') }}
      </div>
      <template v-else>
        <div
          v-for="(holiday, index) in holidays"
          :key="holiday.id"
          class="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3"
          :class="{ 'border-b border-n-weak': index !== holidays.length - 1 }"
        >
          <span class="truncate text-sm text-n-slate-12">{{
            holiday.name
          }}</span>
          <span class="text-sm text-n-slate-11">{{
            formatDateTime(holiday.start_date)
          }}</span>
          <span class="text-sm text-n-slate-11">{{
            formatDateTime(holiday.end_date)
          }}</span>
          <div class="flex items-center gap-2">
            <Button
              sm
              slate
              outline
              :label="t('BUSINESS_HOURS.HOLIDAYS.EDIT')"
              @click="editHoliday(holiday)"
            />
            <Button
              sm
              slate
              outline
              :label="t('BUSINESS_HOURS.HOLIDAYS.DELETE')"
              :is-loading="isDeletingId === holiday.id"
              @click="deleteHoliday(holiday.id)"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
